import { ArrowRightStartOnRectangleIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { Avatar } from '@heroui/avatar';
import { Button } from '@heroui/button';
import { Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger } from '@heroui/dropdown';
import {
  Navbar as HeroUINavbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
} from '@heroui/navbar';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { useAuth } from '../firebase/useAuth';

const menuItems = [
  { name: 'Dashboard', href: '/' },
  { name: 'Site Lists', href: '/site-lists' },
  { name: 'Sites', href: '/sites' },
  { name: 'Bills', href: '/bills' },
  { name: 'Contracts', href: '/contracts' },
  { name: 'Tenders', href: '/tenders' },
];

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <HeroUINavbar onMenuOpenChange={setIsMenuOpen} isBordered maxWidth="xl">
      <NavbarContent>
        <NavbarMenuToggle aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} className="sm:hidden" />
        <NavbarBrand>
          <p className="font-bold text-inherit">Relay</p>
        </NavbarBrand>
      </NavbarContent>

      <NavbarContent className="hidden sm:flex gap-4" justify="center">
        <NavbarItem isActive={location.pathname === '/'}>
          <Link to="/" className={location.pathname === '/' ? 'text-primary' : 'text-foreground'}>
            Dashboard
          </Link>
        </NavbarItem>
        <NavbarItem isActive={location.pathname === '/site-lists'}>
          <Link to="/site-lists" className={location.pathname === '/site-lists' ? 'text-primary' : 'text-foreground'}>
            Site Lists
          </Link>
        </NavbarItem>
        <NavbarItem isActive={location.pathname === '/sites'}>
          <Link to="/sites" className={location.pathname === '/sites' ? 'text-primary' : 'text-foreground'}>
            Sites
          </Link>
        </NavbarItem>
        <NavbarItem isActive={location.pathname === '/bills'}>
          <Link to="/bills" className={location.pathname === '/bills' ? 'text-primary' : 'text-foreground'}>
            Bills
          </Link>
        </NavbarItem>
        <NavbarItem isActive={location.pathname === '/contracts'}>
          <Link to="/contracts" className={location.pathname === '/contracts' ? 'text-primary' : 'text-foreground'}>
            Contracts
          </Link>
        </NavbarItem>
      </NavbarContent>

      <NavbarContent justify="end">
        {currentUser && (
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Avatar
                isBordered
                as="button"
                className="transition-transform"
                src={currentUser.photoURL ?? undefined}
                name={currentUser.displayName ?? currentUser.email ?? 'User'}
                size="sm"
              />
            </DropdownTrigger>
            <DropdownMenu aria-label="Profile Actions" variant="flat">
              <DropdownSection showDivider>
                <DropdownItem key="profile" isReadOnly className="opacity-100">
                  <p className="font-semibold">{currentUser.displayName ?? 'User'}</p>
                  <p className="text-sm text-default-500">{currentUser.email}</p>
                </DropdownItem>
              </DropdownSection>
              <DropdownSection showDivider>
                <DropdownItem
                  key="settings"
                  startContent={<Cog6ToothIcon className="h-4 w-4" />}
                  onPress={() => navigate({ to: '/settings' })}
                >
                  Settings
                </DropdownItem>
              </DropdownSection>
              <DropdownSection>
                <DropdownItem
                  key="logout"
                  color="danger"
                  startContent={<ArrowRightStartOnRectangleIcon className="h-4 w-4" />}
                  onPress={handleLogout}
                >
                  Log Out
                </DropdownItem>
              </DropdownSection>
            </DropdownMenu>
          </Dropdown>
        )}
      </NavbarContent>

      <NavbarMenu>
        {menuItems.map((item, index) => (
          <NavbarMenuItem key={`${item.name}-${index}`}>
            <Link
              className={`w-full text-lg ${location.pathname === item.href ? 'text-primary' : 'text-foreground'}`}
              to={item.href}
            >
              {item.name}
            </Link>
          </NavbarMenuItem>
        ))}
        {currentUser && (
          <NavbarMenuItem>
            <Button className="w-full justify-start" color="danger" variant="light" onPress={handleLogout}>
              Log Out
            </Button>
          </NavbarMenuItem>
        )}
      </NavbarMenu>
    </HeroUINavbar>
  );
}
